<script>
    import { createEventDispatcher } from "svelte";
    import { Section, Button } from "../../lib/ui";
    import ImagePicker from "../chat-builder/ImagePicker.svelte";
    import { botAssetFolder } from "../chat-builder/imageAssetApi.js";
    import {
        MAP_PARAMS,
        MAP_OVERRIDE_GROUPS,
        MAP_MODES,
        MAP_GOAL_TYPES,
        MAP_UNLOCK_TYPES,
        ASSET_SET_STEPS,
        SLOT_COUNT,
        newMap,
    } from "./gameSlots.js";
    import AssetSetBatch from "./AssetSetBatch.svelte";
    import StageList from "./StageList.svelte";

    export let bot;
    /** 저작된 맵 목록. 비어 있으면 게임은 내장 기본 맵 1개로 논다. */
    export let maps = [];
    /** 고를 수 있는 편대(=[적 편대] 탭에서 만든 것). 맵마다 일부만 쓰게 할 수 있다. */
    export let patterns = [];
    /**
     * 고를 수 있는 드롭 스킬 = [스킬] 탭에서 만든 것 중 **'떨어지게 하기'를 켠 것만**.
     * 안 켠 스킬은 캐릭터 스킬 전용이라 맵에서 고를 대상이 아니다.
     */
    export let dropSkills = [];
    /** assetId → URL. 오버라이드 썸네일용(저장하지 않는다). */
    export let urls = {};

    const dispatch = createEventDispatcher();

    let pickerOpen = false;
    // ⚠️ 고르는 자리가 둘이다 — 맵 그림 오버라이드(slot)와 **스테이지 보스 그림**(stage/field).
    //    한 상태로 섞으면 보스 그림을 골랐는데 맵 배경이 바뀌는 식이 된다.
    let pickerTarget = null; // { kind:'mapAsset', index, slot } | { kind:'boss', index, stage, field }

    const emit = (next) => dispatch("change", next);

    const updateAt = (index, patch) =>
        emit(maps.map((m, i) => (i === index ? { ...m, ...patch } : m)));

    const updateParam = (index, key, value) =>
        emit(
            maps.map((m, i) =>
                i === index ? { ...m, params: { ...(m.params || {}), [key]: value } } : m,
            ),
        );

    const resetParam = (index, key) =>
        emit(
            maps.map((m, i) => {
                if (i !== index) return m;
                const params = { ...(m.params || {}) };
                delete params[key];
                return { ...m, params };
            }),
        );

    const setMode = (index, mode) => {
        // 목표형으로 바꾸면 목표가 있어야 한다 — 없으면 "끝이 없는 목표형 맵"이 된다.
        const goal = mode === "goal" ? maps[index].goal || { type: "score", value: 30000 } : null;
        updateAt(index, { mode, goal });
    };

    const setGoalType = (index, type) => {
        const preset = MAP_GOAL_TYPES.find((g) => g.value === type);
        updateAt(index, { goal: { type, value: preset?.def ?? 0 } });
    };

    const setGoalValue = (index, value) => {
        const m = maps[index];
        updateAt(index, {
            goal: { type: m.goal?.type || "score", value: Math.max(1, Math.floor(Number(value) || 0)) },
        });
    };

    const togglePattern = (index, patternId) => {
        const cur = maps[index].patternIds || [];
        const next = cur.includes(patternId)
            ? cur.filter((p) => p !== patternId)
            : [...cur, patternId];
        updateAt(index, { patternIds: next });
    };

    const toggleSkill = (index, skillId) => {
        const cur = maps[index].skillIds || [];
        const next = cur.includes(skillId)
            ? cur.filter((s) => s !== skillId)
            : [...cur, skillId];
        updateAt(index, { skillIds: next });
    };

    /**
     * 고른 스킬이 **전부 사라졌는지**([스킬] 탭에서 지웠거나 '떨어지게 하기'를 껐다).
     * 런타임은 이때 전역으로 되돌리지 않고 **아이템 없는 맵**으로 논다 — 저작자가 일부러 뺀 걸
     * 되살리지 않으려는 것인데, 말없이 두면 "왜 아이템이 안 나오지"가 된다.
     */
    const pickedAllGone = (m) =>
        (m.skillIds || []).length > 0 &&
        !(m.skillIds || []).some((id) => dropSkills.some((s) => s.id === id));

    const addMap = () => emit([...maps, newMap(maps.length)]);
    const removeMap = (index) => emit(maps.filter((_, i) => i !== index));

    const move = (index, dir) => {
        const to = index + dir;
        if (to < 0 || to >= maps.length) return;
        const next = [...maps];
        [next[index], next[to]] = [next[to], next[index]];
        emit(next);
    };

    const openPicker = (index, slot) => {
        pickerTarget = { kind: "mapAsset", index, slot };
        pickerOpen = true;
    };

    const openBossPicker = (index) => (e) => {
        pickerTarget = { kind: "boss", index, stage: e.detail.index, field: e.detail.field };
        pickerOpen = true;
    };

    const onStagesChange = (index) => (e) => updateAt(index, { stages: e.detail });

    const onPick = (e) => {
        const { assetId, url } = e.detail;
        pickerOpen = false;
        const target = pickerTarget;
        pickerTarget = null;
        if (!target) return;
        dispatch("assetUrl", { assetId, url });

        if (target.kind === "boss") {
            const stages = (maps[target.index].stages || []).map((st, k) =>
                k === target.stage ? { ...st, boss: { ...(st.boss || {}), [target.field]: assetId } } : st,
            );
            updateAt(target.index, { stages });
            return;
        }
        updateAt(target.index, {
            assets: { ...(maps[target.index].assets || {}), [target.slot]: assetId },
        });
    };

    // 세트 일괄 생성 결과를 **이 맵의 오버라이드**로 꽂는다.
    // ⚠️ 슬롯 이름이 전역 세트(ASSET_SET_STEPS)와 같아서 레시피를 그대로 재사용한다 —
    //    들어가는 자리만 전역 ASSETS 가 아니라 m.assets 다.
    const onBatchGenerated = (index) => (e) => {
        const { slot, assetId, url } = e.detail;
        dispatch("assetUrl", { assetId, url });
        updateAt(index, { assets: { ...(maps[index].assets || {}), [slot]: assetId } });
    };

    const clearOverride = (index, slot) => {
        const assets = { ...(maps[index].assets || {}) };
        delete assets[slot];
        updateAt(index, { assets });
    };

    const paramValue = (m, p) => {
        const v = m.params?.[p.key];
        return typeof v === "number" && Number.isFinite(v) ? v : p.def;
    };
    const isParamChanged = (m, p) => paramValue(m, p) !== p.def;
    const fmtParam = (p, v) => (p.kind === "delta" ? (v > 0 ? `+${v}` : String(v)) : `×${v.toFixed(1)}`);

    /* ── 해금 조건 ─────────────────────────────────────────────────────────
     * ⚠️ 정규화 규칙은 런타임 progress.ts `resolveUnlock` · 서버 util/gameProgress.js 와 같다.
     *    **안 정한 옛 맵**은 "가격이 있으면 코인, 없으면 처음부터"로 읽어야 기존 게임의 해금이
     *    조용히 바뀌지 않는다. */
    const unlockOf = (m) => {
        if (m.unlock && typeof m.unlock === "object") return m.unlock;
        return (Number(m.price) || 0) > 0 ? { type: "coin" } : { type: "always" };
    };

    const setUnlockType = (index, type) => {
        // 종류를 바꾸면 앞 종류가 쓰던 값은 버린다 — 남겨두면 "레벨 3"이 "3점"으로 되살아난다.
        const preset =
            type === "score" ? { value: 50000 } : type === "level" ? { value: 3 } : {};
        updateAt(index, { unlock: { type, ...preset } });
    };

    const setUnlockField = (index, patch) =>
        updateAt(index, { unlock: { ...unlockOf(maps[index]), ...patch } });

    /** 처음부터 열려 있는 맵. 하나도 없으면 아무도 게임을 시작할 수 없다. */
    $: openCount = maps.filter((m) => {
        const u = unlockOf(m);
        return u.type === "always" || (u.type === "coin" && (Number(m.price) || 0) <= 0);
    }).length;

    const goalUnit = (m) => MAP_GOAL_TYPES.find((g) => g.value === m.goal?.type)?.unit || "";
</script>

<Section
    title="맵"
    description="플레이어가 고르는 스테이지입니다. 게임 화면 위쪽에서 좌우로 넘겨 고르고, 맵마다 난이도·편대·그림·여는 방법이 다릅니다."
>
    <p class="tw-mb-2 tw-text-[13px] tw-leading-relaxed tw-text-muted-foreground">
        난이도는 절대값이 아니라 <strong class="tw-text-foreground">[난이도] 탭에서 정한 값에 곱해지는 배율</strong>입니다.
        어려운 맵일수록 곱하는 수를 크게 두세요. 전역 난이도를 바꾸면 맵들도 같이 따라옵니다.
    </p>
    <p class="tw-text-[13px] tw-leading-relaxed tw-text-muted-foreground">
        <strong class="tw-text-foreground">여는 방법은 맵마다 고릅니다</strong> — 앞 맵을 깨야 열리게 할 수도,
        코인으로 사게 할 수도 있습니다. 실력으로 밟아 올라가는 게임과 오래 해서 여는 게임이 서로 다르기 때문입니다.
        잠긴 맵도 넘겨볼 수는 있고, <strong class="tw-text-foreground">무엇을 하면 열리는지</strong>가 함께 보입니다.
    </p>

    {#if maps.length === 0}
        <div class="tw-mt-4 tw-rounded-lg tw-border tw-border-dashed tw-border-border tw-bg-muted/40 tw-p-5">
            <div class="tw-mb-1 tw-text-sm tw-font-bold tw-text-foreground">아직 만든 맵이 없어요</div>
            <div class="tw-text-[13px] tw-leading-relaxed tw-text-muted-foreground">
                지금은 내장 기본 맵 1개(무한)로 게임이 돌아갑니다. 하나라도 만들면 그때부터는
                <strong class="tw-text-foreground">여기 만든 것만</strong> 나옵니다.
            </div>
        </div>
    {:else if openCount === 0}
        <div class="tw-mt-4 tw-text-xs tw-text-amber-600 dark:tw-text-amber-400">
            처음부터 열려 있는 맵이 하나도 없습니다. 아무도 게임을 시작할 수 없어요 —
            하나는 여는 방법을 '처음부터'로 두세요.
        </div>
    {/if}
</Section>

{#each maps as m, i (m.id)}
    <Section>
        <div class="tw-mb-4 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <input
                class="tw-h-9 tw-min-w-0 tw-flex-1 tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm tw-text-foreground"
                type="text"
                placeholder="맵 이름 (예: 새벽 하늘)"
                value={m.name || ""}
                on:change={(e) => updateAt(i, { name: e.target.value })}
            />
            <button type="button" class="tw-h-9 tw-w-9 tw-cursor-pointer tw-rounded-md tw-border tw-border-border tw-bg-background tw-text-xs tw-text-muted-foreground hover:tw-text-foreground" title="위로" on:click={() => move(i, -1)}>↑</button>
            <button type="button" class="tw-h-9 tw-w-9 tw-cursor-pointer tw-rounded-md tw-border tw-border-border tw-bg-background tw-text-xs tw-text-muted-foreground hover:tw-text-foreground" title="아래로" on:click={() => move(i, 1)}>↓</button>
            <button type="button" class="tw-h-9 tw-cursor-pointer tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-xs tw-text-muted-foreground hover:tw-border-destructive hover:tw-text-destructive" on:click={() => removeMap(i)}>삭제</button>
        </div>

        <!-- 여는 방법 · 방식 -->
        <div class="tw-mb-5 tw-flex tw-flex-wrap tw-gap-4">
            <label class="tw-flex tw-flex-col tw-gap-1.5">
                <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">여는 방법</span>
                <select
                    class="tw-h-9 tw-w-[180px] tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-2 tw-text-sm tw-text-foreground"
                    value={unlockOf(m).type}
                    on:change={(e) => setUnlockType(i, e.target.value)}
                >
                    {#each MAP_UNLOCK_TYPES as u (u.value)}
                        <option value={u.value}>{u.label}</option>
                    {/each}
                </select>
                <span class="tw-text-xs tw-text-muted-foreground">
                    {MAP_UNLOCK_TYPES.find((x) => x.value === unlockOf(m).type)?.hint}
                </span>
            </label>

            {#if unlockOf(m).type === "coin"}
                <label class="tw-flex tw-flex-col tw-gap-1.5">
                    <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">가격 (코인)</span>
                    <input
                        class="tw-h-9 tw-w-[140px] tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm tw-text-foreground"
                        type="number" min="0" step="10"
                        value={m.price ?? 0}
                        on:change={(e) => updateAt(i, { price: Math.max(0, Math.floor(Number(e.target.value) || 0)) })}
                    />
                    <span class="tw-text-xs tw-text-muted-foreground">
                        {(Number(m.price) || 0) <= 0 ? "0이면 처음부터 쓸 수 있어요" : "코인을 모아야 열립니다"}
                    </span>
                </label>
            {:else if unlockOf(m).type === "clear"}
                <label class="tw-flex tw-flex-col tw-gap-1.5">
                    <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">깨야 하는 맵</span>
                    <select
                        class="tw-h-9 tw-w-[200px] tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-2 tw-text-sm tw-text-foreground"
                        value={unlockOf(m).mapId || ""}
                        on:change={(e) => setUnlockField(i, { mapId: e.target.value || undefined })}
                    >
                        <option value="">바로 앞 맵</option>
                        {#each maps.filter((x) => x.id !== m.id) as other (other.id)}
                            <option value={other.id}>{other.name || "이름 없는 맵"}</option>
                        {/each}
                    </select>
                    <span class="tw-text-xs tw-text-muted-foreground">
                        ⚠️ 깨야 하는 맵은 <strong class="tw-text-foreground">목표 달성형</strong>이어야 합니다 —
                        무한 맵은 끝이 없어서 영영 안 열립니다.
                    </span>
                </label>
            {:else if unlockOf(m).type === "score"}
                <label class="tw-flex tw-flex-col tw-gap-1.5">
                    <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">누적 점수</span>
                    <input
                        class="tw-h-9 tw-w-[160px] tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm tw-text-foreground"
                        type="number" min="0" step="1000"
                        value={unlockOf(m).value ?? 0}
                        on:change={(e) => setUnlockField(i, { value: Math.max(0, Math.floor(Number(e.target.value) || 0)) })}
                    />
                    <span class="tw-text-xs tw-text-muted-foreground">여태 쌓은 점수의 합계 기준입니다.</span>
                </label>
            {:else if unlockOf(m).type === "level"}
                <label class="tw-flex tw-flex-col tw-gap-1.5">
                    <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">레벨</span>
                    <input
                        class="tw-h-9 tw-w-[120px] tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm tw-text-foreground"
                        type="number" min="1" step="1"
                        value={unlockOf(m).value ?? 1}
                        on:change={(e) => setUnlockField(i, { value: Math.max(1, Math.floor(Number(e.target.value) || 1)) })}
                    />
                    <span class="tw-text-xs tw-text-muted-foreground">
                        레벨 곡선은 [난이도] 탭에서 정합니다.
                    </span>
                </label>
            {/if}

            <label class="tw-flex tw-flex-col tw-gap-1.5">
                <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">방식</span>
                <select
                    class="tw-h-9 tw-w-[180px] tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-2 tw-text-sm tw-text-foreground"
                    value={m.mode || "endless"}
                    on:change={(e) => setMode(i, e.target.value)}
                >
                    {#each MAP_MODES as mode (mode.value)}
                        <option value={mode.value}>{mode.label}</option>
                    {/each}
                </select>
                <span class="tw-text-xs tw-text-muted-foreground">
                    {MAP_MODES.find((x) => x.value === (m.mode || "endless"))?.hint}
                </span>
            </label>

            {#if m.mode === "goal"}
                <label class="tw-flex tw-flex-col tw-gap-1.5">
                    <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">목표</span>
                    <div class="tw-flex tw-gap-2">
                        <select
                            class="tw-h-9 tw-w-[130px] tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-2 tw-text-sm tw-text-foreground"
                            value={m.goal?.type || "score"}
                            on:change={(e) => setGoalType(i, e.target.value)}
                        >
                            {#each MAP_GOAL_TYPES as g (g.value)}
                                <option value={g.value}>{g.label}</option>
                            {/each}
                        </select>
                        <input
                            class="tw-h-9 tw-w-[120px] tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm tw-text-foreground"
                            type="number" min="1"
                            value={m.goal?.value ?? 0}
                            on:change={(e) => setGoalValue(i, e.target.value)}
                        />
                    </div>
                    <span class="tw-text-xs tw-text-muted-foreground">
                        {m.goal?.value ?? 0}{goalUnit(m)}를 넘기면 클리어
                    </span>
                </label>
            {/if}
        </div>

        <!-- 난이도 보정 -->
        <div class="tw-mb-5">
            <div class="tw-mb-2 tw-text-[13px] tw-font-semibold tw-text-foreground">난이도 보정</div>
            <div class="tw-flex tw-flex-col tw-gap-3">
                {#each MAP_PARAMS as p (p.key)}
                    <div>
                        <div class="tw-mb-1 tw-flex tw-items-center tw-justify-between tw-gap-2">
                            <span class="tw-text-[13px] tw-text-foreground">{p.label}</span>
                            <span class="tw-flex tw-items-center tw-gap-2 tw-text-[13px] tw-font-bold {isParamChanged(m, p) ? 'tw-text-primary' : 'tw-text-muted-foreground'}">
                                {fmtParam(p, paramValue(m, p))}
                                {#if isParamChanged(m, p)}
                                    <button type="button" class="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-text-[11px] tw-font-normal tw-text-muted-foreground tw-underline" on:click={() => resetParam(i, p.key)}>되돌리기</button>
                                {/if}
                            </span>
                        </div>
                        <input
                            class="tw-w-full" style="accent-color: hsl(var(--sb-primary))"
                            type="range" min={p.min} max={p.max} step={p.step}
                            value={paramValue(m, p)}
                            on:change={(e) => updateParam(i, p.key, Number(e.target.value))}
                        />
                        <div class="tw-mt-1 tw-text-[11px] tw-text-muted-foreground">{p.hint}</div>
                    </div>
                {/each}
            </div>
        </div>

        <!-- 전용 편대 -->
        <div class="tw-mb-5">
            <div class="tw-mb-1 tw-text-[13px] tw-font-semibold tw-text-foreground">이 맵에 나오는 편대</div>
            <div class="tw-mb-2 tw-text-xs tw-text-muted-foreground">
                아무것도 고르지 않으면 <strong class="tw-text-foreground">[적 편대] 탭의 전부</strong>가 나옵니다.
            </div>
            {#if patterns.length === 0}
                <div class="tw-text-xs tw-text-muted-foreground">
                    [적 편대] 탭에서 편대를 만들면 여기서 고를 수 있어요. (지금은 내장 기본 편대로 나옵니다)
                </div>
            {:else}
                <div class="tw-flex tw-flex-wrap tw-gap-2">
                    {#each patterns as p (p.id)}
                        {@const on = (m.patternIds || []).includes(p.id)}
                        <button
                            type="button"
                            class="tw-cursor-pointer tw-rounded-full tw-border tw-px-3 tw-py-1.5 tw-text-xs tw-transition-colors {on
                                ? 'tw-border-primary tw-bg-primary/10 tw-text-primary'
                                : 'tw-border-border tw-bg-background tw-text-muted-foreground hover:tw-text-foreground'}"
                            on:click={() => togglePattern(i, p.id)}
                        >
                            {p.name || "이름 없음"}
                        </button>
                    {/each}
                </div>
            {/if}
        </div>

        <!-- 스테이지 (맵 ⊃ 스테이지 ⊃ 편대) -->
        <div class="tw-mb-5 tw-rounded-lg tw-border tw-border-border tw-bg-sidebar/40 tw-p-4">
            <StageList
                stages={m.stages || []}
                patterns={(m.patternIds || []).length > 0
                    ? patterns.filter((p) => (m.patternIds || []).includes(p.id))
                    : patterns}
                {urls}
                on:change={onStagesChange(i)}
                on:pick={openBossPicker(i)}
            />
        </div>

        <!-- 전용 드롭 스킬 -->
        <div class="tw-mb-5">
            <div class="tw-mb-1 tw-text-[13px] tw-font-semibold tw-text-foreground">이 맵에서 떨어지는 스킬</div>
            <div class="tw-mb-2 tw-text-xs tw-text-muted-foreground">
                아무것도 고르지 않으면 <strong class="tw-text-foreground">[스킬] 탭에서 '떨어지게 하기'를 켠 전부</strong>가 나옵니다.
                무엇이 더 자주 나올지(비중)는 [스킬] 탭에서 정합니다 — 여기서는 <strong class="tw-text-foreground">무엇이 나올지</strong>만 고릅니다.
            </div>
            {#if dropSkills.length === 0}
                <div class="tw-text-xs tw-text-muted-foreground">
                    [스킬] 탭에서 '적을 잡으면 떨어지게 하기'를 켜면 여기서 고를 수 있어요.
                </div>
            {:else}
                <div class="tw-flex tw-flex-wrap tw-gap-2">
                    {#each dropSkills as s (s.id)}
                        {@const on = (m.skillIds || []).includes(s.id)}
                        <button
                            type="button"
                            class="tw-cursor-pointer tw-rounded-full tw-border tw-px-3 tw-py-1.5 tw-text-xs tw-transition-colors {on
                                ? 'tw-border-primary tw-bg-primary/10 tw-text-primary'
                                : 'tw-border-border tw-bg-background tw-text-muted-foreground hover:tw-text-foreground'}"
                            on:click={() => toggleSkill(i, s.id)}
                        >
                            {s.name || "이름 없음"}
                        </button>
                    {/each}
                </div>
                {#if (m.skillIds || []).length > SLOT_COUNT}
                    <div class="tw-mt-2 tw-text-xs tw-text-amber-600 dark:tw-text-amber-400">
                        아이템 칸이 {SLOT_COUNT}개라 이 맵에서도 <strong>위에서부터 {SLOT_COUNT}개</strong>만 떨어집니다.
                    </div>
                {/if}
                {#if pickedAllGone(m)}
                    <div class="tw-mt-2 tw-text-xs tw-text-amber-600 dark:tw-text-amber-400">
                        고른 스킬이 전부 없어졌습니다([스킬] 탭에서 지웠거나 '떨어지게 하기'를 껐어요).
                        지금 이 맵에서는 아이템이 하나도 안 떨어집니다 — 다시 고르거나, 전부 꺼서 전역을 쓰세요.
                    </div>
                {/if}
            {/if}
        </div>

        <!-- 그림 오버라이드 -->
        <div>
            <div class="tw-mb-1 tw-text-[13px] tw-font-semibold tw-text-foreground">이 맵만의 그림</div>
            <div class="tw-mb-3">
                <AssetSetBatch
                    chatId={bot.id}
                    steps={ASSET_SET_STEPS}
                    folderPath={botAssetFolder(bot)}
                    scopeLabel={`'${m.name || "이 맵"}' 의 배경·코인·적`}
                    on:generated={onBatchGenerated(i)}
                />
            </div>
            <div class="tw-mb-3 tw-text-xs tw-text-muted-foreground">
                넣은 것만 이 맵에서 바뀝니다. 비운 자리는 <strong class="tw-text-foreground">[기본 이미지] 탭의 그림</strong>이 그대로 나옵니다.
                적은 <strong class="tw-text-foreground">기본·맞을 때·죽을 때를 함께</strong> 넣어야 맵 안에서 어긋나지 않습니다.
            </div>
            {#each MAP_OVERRIDE_GROUPS as group, gi (group.title)}
                <div class="tw-flex tw-flex-col tw-gap-3 tw-py-3 sm:tw-flex-row sm:tw-gap-5 {gi > 0 ? 'tw-border-t tw-border-border' : 'tw-pt-0'}">
                    <div class="tw-flex tw-w-[110px] tw-shrink-0 tw-flex-col tw-gap-1 sm:tw-pt-2">
                        {#if group.color}
                            <span class="tw-h-2.5 tw-w-2.5 tw-rounded-full" style="background:{group.color}"></span>
                        {/if}
                        <span class="tw-text-[13px] tw-font-bold tw-text-foreground">{group.title}</span>
                    </div>
                    <div class="tw-flex tw-flex-wrap tw-gap-4">
                        {#each group.slots as slot (slot.key)}
                            {@const assetId = m.assets?.[slot.key]}
                            <div class="tw-flex tw-w-[96px] tw-flex-col tw-items-center tw-gap-1">
                                <button
                                    type="button"
                                    class="map-thumb {assetId && urls[assetId] ? 'tw-border-border' : 'tw-border-dashed tw-border-border tw-bg-muted'}"
                                    on:click={() => openPicker(i, slot.key)}
                                >
                                    {#if assetId && urls[assetId]}
                                        <img crossorigin="anonymous" class="tw-max-h-full tw-max-w-full tw-object-contain" src={urls[assetId]} alt="" />
                                    {:else}
                                        <span class="tw-text-xl tw-text-muted-foreground">+</span>
                                    {/if}
                                </button>
                                <div class="tw-text-xs tw-font-semibold tw-text-foreground">{slot.label}</div>
                                {#if assetId}
                                    <button type="button" class="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-text-[11px] tw-text-muted-foreground tw-underline" on:click={() => clearOverride(i, slot.key)}>기본으로</button>
                                {/if}
                            </div>
                        {/each}
                    </div>
                </div>
            {/each}
        </div>
    </Section>
{/each}

<div>
    <Button variant="outline" on:click={addMap}>+ 새 맵 만들기</Button>
</div>

<ImagePicker
    bind:open={pickerOpen}
    chatId={bot.id}
    contextFolder={botAssetFolder(bot)}
    mode="single"
    currentAssetId={pickerTarget?.kind === "boss"
        ? maps[pickerTarget.index]?.stages?.[pickerTarget.stage]?.boss?.[pickerTarget.field] ?? null
        : pickerTarget
          ? (maps[pickerTarget.index]?.assets?.[pickerTarget.slot] ?? null)
          : null}
    on:select={onPick}
/>

<style>
    /* 투명 PNG 가 대부분이라 밝은 바탕에선 안 보인다 — 게임 화면과 비슷한 어두운 바탕에 둔다. */
    .map-thumb {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 96px;
        height: 96px;
        padding: 0;
        border-width: 1px;
        border-style: solid;
        border-radius: 12px;
        background: #0d1430;
        cursor: pointer;
        overflow: hidden;
    }

    .map-thumb:hover {
        border-color: hsl(var(--sb-primary));
    }
</style>
